<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { companyIdentity } from '@jws/shared';
import { RouterLink } from 'vue-router';
import companyIntroImage from '../assets/company-intro-building.jpg';
import organizationTeamImage from '../assets/company-organization-hangzhou-rd-team.jpg';
import { getBootstrap, type Contact } from '../api';
import { isZh, locale, text } from '../i18n';

type LocalText = { zh: string; en: string };

const localized = (value: LocalText) => isZh.value ? value.zh : value.en;
const contact = ref<Contact | null>(null);
const companyName = computed(() => contact.value?.companyName || text(companyIdentity.zh.name, companyIdentity.en.name));
const primaryChannels = computed(() => {
  const channels = contact.value?.channels ?? [];
  const selected = channels.filter((channel) => channel.isPrimary);
  return (selected.length ? selected : channels).slice(0, 3);
});
const primaryPhone = computed(() => primaryChannels.value.find((channel) => channel.type === 'phone') || primaryChannels.value[0] || null);

watch(locale, (currentLocale) => { void loadContact(currentLocale); }, { immediate: true });

async function loadContact(currentLocale: 'zh-CN' | 'en-US') {
  try {
    contact.value = (await getBootstrap(currentLocale)).contact;
  } catch {
    contact.value = null;
  }
}

function channelHref(channel: Contact['channels'][number]): string | null {
  if (channel.href) return channel.href;
  if (channel.type === 'phone') return `tel:${channel.value.replace(/[^+\d]/g, '')}`;
  if (channel.type === 'email') return `mailto:${channel.value}`;
  if ((channel.type === 'social' || channel.type === 'website') && /^https?:\/\//i.test(channel.value)) return channel.value;
  return null;
}

function isExternalHref(href: string | null): boolean { return Boolean(href && /^https?:\/\//i.test(href)); }

const capabilities = [
  { title: { zh: '扫描成像研发', en: 'Imaging R&D' }, copy: { zh: '围绕微剂量成像、图像处理和设备控制持续迭代。', en: 'Continuous work in micro-dose imaging, image processing and equipment control.' } },
  { title: { zh: '智能识别算法', en: 'Intelligent recognition' }, copy: { zh: '提升异常区域提示、图像复核与操作效率。', en: 'Improves anomaly indication, image review and operator efficiency.' } },
  { title: { zh: '业务平台集成', en: 'Platform integration' }, copy: { zh: '连接人员、设备、图像、记录与业务流程。', en: 'Connects people, equipment, images, records and operational workflows.' } },
  { title: { zh: '项目交付服务', en: 'Project delivery' }, copy: { zh: '覆盖现场勘察、部署调试、培训与持续支持。', en: 'Site assessment, commissioning, training and ongoing support.' } }
];

const values = [
  { title: { zh: '规范测试', en: 'Structured testing' }, copy: { zh: '关键功能与运行状态逐项验证', en: 'Verify key functions and operating states' } },
  { title: { zh: '透明交付', en: 'Transparent delivery' }, copy: { zh: '实施计划、培训和验收边界清晰', en: 'Clear implementation, training and acceptance' } },
  { title: { zh: '持续服务', en: 'Ongoing support' }, copy: { zh: '问题响应、远程支持与维护协同', en: 'Responsive remote support and maintenance' } }
];
</script>

<template>
  <section class="portal-section">
      <div class="container portal-company-intro">
        <div class="portal-company-intro__visual">
          <header class="portal-section-heading portal-section-heading--stacked">
            <span>{{ text('公司简介', 'Company profile') }}</span><h2>{{ companyName }}</h2>
          </header>
          <figure class="portal-company-intro__media">
            <img
              :src="companyIntroImage"
              :alt="text('杭州技卫士总部办公楼', 'JWS Hangzhou headquarters building')"
            >
          </figure>
        </div>
        <div class="portal-company-intro__copy">
          <p>{{ text('JWS 是一家富有创新力的科技企业，专注于微剂量X光扫描成像技术、光学精密探测技术的研发，以及微剂量X光人体扫描设备的生产制造与集成开发。公司致力于为客户提供智能安检管理平台和综合型智能安检系统解决方案。', 'JWS is a technology company specializing in micro-dose X-ray imaging, precision optical detection and integrated body screening equipment.') }}</p>
          <p>{{ text('我们与浙江大学光电信息学院光电工程研究所共建国家实验室。依托X光扫描成像技术的研究成果与领先优势，率先研发人体X光扫描成像所需的人工智能图像识别、图像处理算法及自动目标识别与报警技术。', 'We collaborate with optical engineering researchers and turn X-ray imaging research into intelligent recognition, image processing and automatic alarm technology.') }}</p>
          <p>{{ text('JWS 汇聚了国内相关领域的多位领军人才，为公司的运营、产品研发与市场拓展提供有力支撑。微剂量X光扫描成像技术、图像处理算法及智能图像识别技术均持续面向行业前沿迭代。', 'Our team brings together experienced specialists in operations, product engineering and market delivery, with sustained work at the leading edge of the sector.') }}</p>
          <p>{{ text('JWS 产品现已广泛应用于公安、监狱、戒毒、监管、边检及矿业等领域，并获得用户认可。', 'JWS products are used across public security, corrections, border inspection and mining environments.') }}</p>
        </div>
      </div>
  </section>

  <section class="portal-section portal-section--soft">
      <div class="container">
        <header class="portal-section-heading">
          <span>{{ text('核心能力', 'Core capabilities') }}</span><h2>{{ text('把研发成果转化为稳定的项目能力', 'Turn engineering into dependable project capability') }}</h2>
        </header>
        <div class="portal-capability-grid">
          <article
            v-for="(item, index) in capabilities"
            :key="localized(item.title)"
          >
            <span>{{ String(index + 1).padStart(2, '0') }}</span><h3>{{ localized(item.title) }}</h3><p>{{ localized(item.copy) }}</p>
          </article>
        </div>
      </div>
  </section>

  <section class="portal-section">
      <div class="container portal-location-grid">
        <figure class="portal-location-grid__visual">
          <img
            :src="organizationTeamImage"
            :alt="text('杭州总部团队合照', 'JWS Hangzhou headquarters team')"
          >
          <figcaption>{{ text('杭州总部团队', 'Hangzhou headquarters team') }}</figcaption>
        </figure>
        <div class="portal-location-grid__content">
          <h2>{{ text('杭州总部', 'Hangzhou Headquarters') }}</h2>
          <p class="portal-headquarters-address">{{ text(companyIdentity.zh.address, companyIdentity.en.address) }}</p>
          <div class="portal-location-functions">
            <div>
              <h3>{{ text('研发与技术', 'Research & Development') }}</h3>
              <p>{{ text('产品研发、系统验证、技术创新与支持', 'Product Development · System Validation · Technological Innovation · Technical Support') }}</p>
            </div>
            <div>
              <h3>{{ text('营销与服务', 'Marketing & Customer Services') }}</h3>
              <p>{{ text('市场拓展、商务合作、客户服务与项目协同', 'Market Development · Business Partnerships · Customer Service · Project Coordination') }}</p>
            </div>
          </div>
        </div>
      </div>
  </section>

  <section class="portal-section portal-section--dark">
      <div class="container">
        <header class="portal-section-heading">
          <span>{{ text('质量与服务', 'Quality and service') }}</span><h2>{{ text('以可验证的过程建立客户信任', 'Build trust through verifiable processes') }}</h2><p>{{ text('从产品测试、现场实施到售后支持，关键节点均形成明确记录和责任闭环。', 'Clear records and accountability across testing, implementation and support.') }}</p>
        </header>
        <div class="portal-values-grid">
          <div
            v-for="item in values"
            :key="localized(item.title)"
          >
            <strong>{{ localized(item.title) }}</strong><span>{{ localized(item.copy) }}</span>
          </div>
        </div>
      </div>
  </section>

  <section class="portal-section portal-company-contact">
      <div class="container">
        <header class="portal-section-heading">
          <span>{{ text('营销中心', 'Sales team') }}</span><h2>{{ text('让项目沟通更直接、更清晰', 'Direct, clear project communication') }}</h2><p>{{ text('设备选型、方案沟通、现场条件确认及售后支持，均可通过以下方式联系营销中心。', 'Contact our sales team for product selection, planning, site requirements and support.') }}</p>
        </header>
        <dl v-if="primaryChannels.length" class="portal-contact-cards">
          <div v-for="channel in primaryChannels" :key="`${channel.type}-${channel.label}-${channel.value}`"><dt>{{ channel.label }}</dt><dd><a v-if="channelHref(channel)" :href="channelHref(channel) || undefined" :target="isExternalHref(channelHref(channel)) ? '_blank' : undefined" :rel="isExternalHref(channelHref(channel)) ? 'noreferrer' : undefined">{{ channel.value }}</a><span v-else>{{ channel.value }}</span></dd></div>
        </dl>
      </div>
  </section>

  <section class="portal-contact-cta">
      <div class="container">
        <div><span>{{ text('项目咨询', 'Project inquiry') }}</span><h2>{{ text('需要更具体的设备选型或实施方案？', 'Need a product selection or implementation plan?') }}</h2></div><div>
          <a v-if="primaryPhone && channelHref(primaryPhone)" :href="channelHref(primaryPhone) || undefined">{{ primaryPhone.value }}</a><RouterLink
            class="button button--light"
            to="/contact"
          >
            {{ text('联系营销中心', 'Contact our team') }}
          </RouterLink>
        </div>
      </div>
  </section>
</template>
